import {
  TtsVoiceRaw,
  TtsVoice,
  TtsLanguage,
  TtsSynthesizeRequest,
  TtsSynthesizeResponse,
  UNKNOWN_LANG,
} from '../types/tts'

// `types/tts` is the single source of truth for TtsLanguage; re-export so existing
// importers (`import { TtsLanguage } from '../services/ttsApi'`) keep working.
export type { TtsLanguage } from '../types/tts'

const API_BASE = (window as any).__ENV__?.API_BASE || 'http://localhost:3000'

// Accept a list from either a real array or a delimited `labels` string, and
// drop empties — `labels` values are always strings, so a language/pitch list
// carried there arrives as "es" or "moderate,high".
function toList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean)
  }
  if (typeof value === 'string') {
    return value.split(',').map((v) => v.trim()).filter(Boolean)
  }
  return []
}

// Adapter: map the API VoiceDto onto the language-first frontend view (catalog v4).
// Stock voices are per-language native speakers described by a vocal RANGE
// (soprano/alto/tenor/…) rather than the old brand-voice-plus-pitch model.
export function mapVoiceDto(raw: TtsVoiceRaw): TtsVoice {
  const labels = raw.labels || {}
  const type = labels.type || (raw.voice_id?.startsWith('v_') ? 'cloned' : 'stock')

  // Catalog v4 ships `languages` (array) and `pitches` (array) at the top level.
  // Everything else below is a fallback for older upstreams.
  const languages = toList(raw.languages).length
    ? toList(raw.languages)
    : toList(labels.languages)
  const pitches = toList(raw.pitches).length ? toList(raw.pitches) : toList(labels.pitches)

  // Voices are language-bound in catalog v4, so the first entry IS the voice's
  // language. Deriving it from `target_language`/`labels.*` alone (none of which
  // v4 sends) collapsed the whole catalog into the 'unknown' bucket.
  const language =
    languages[0] ||
    raw.target_language ||
    labels.target_language ||
    labels.language ||
    raw.locale ||
    labels.accent ||
    UNKNOWN_LANG

  // `pitches` doubles as the vocal-range list; use its first entry when the older
  // explicit pitch_class/range fields are absent.
  const range = labels.pitch_class || raw.pitch_class || raw.range || pitches[0] || ''

  // f0 may arrive as a top-level number or a labels string; tolerate both / absence.
  const f0Raw = raw.f0_hz ?? labels.f0_hz
  const f0 = f0Raw != null && f0Raw !== '' ? Number(f0Raw) : NaN

  return {
    ...raw,
    displayName: labels.name || raw.name,
    language,
    type,
    gender: raw.gender || labels.gender || 'unknown',
    range,
    f0: Number.isFinite(f0) ? f0 : undefined,
    isCloned: type === 'cloned',
    // Kept in full: cloned/zero-shot voices legitimately span several languages.
    languages,
    pitches,
    // deprecated — kept so mid-migration consumers still compile.
    pitch_class: raw.pitch || labels.pitch_class || pitches[0] || '',
  }
}

// Languages served with the lowest latency. NOT a streaming capability: every
// supported language streams (the endpoint advertises `streaming: true` for all
// of them), these are just the fastest. Never render this as a badge on the
// language list — it reads as "the others can't stream". The engine behind it is
// never exposed to users.
const LOW_LATENCY_LANGS = new Set(['en', 'fr'])

// GET /v1/tts/languages - returns { languages: [...] }. Public (no auth): reflects
// what the serve pod actually supports. Maps onto the frontend TtsLanguage view.
export async function fetchLanguages(): Promise<TtsLanguage[]> {
  const response = await fetch(`${API_BASE}/v1/tts/languages`)
  if (!response.ok) {
    throw new Error(`Failed to fetch languages: ${response.status}`)
  }
  const data = await response.json()
  const raw = (data.languages || []) as any[]
  return raw.map((l) => ({
    code: l.code,
    name: l.name,
    nativeName: l.nativeName || l.native_name || undefined,
    voiceCount:
      typeof l.voiceCount === 'number'
        ? l.voiceCount
        : typeof l.voice_count === 'number'
          ? l.voice_count
          : undefined,
    // The service advertises `streaming` per language (true for all of them);
    // absence means "assume streamable" rather than "not streamable".
    streaming: typeof l.streaming === 'boolean' ? l.streaming : true,
    lowLatency: LOW_LATENCY_LANGS.has(l.code),
  }))
}

// GET /v1/tts/voices - returns { voices: VoiceDto[] }
export async function fetchVoices(token: string): Promise<TtsVoice[]> {
  const response = await fetch(`${API_BASE}/v1/tts/voices`, {
    headers: { 'Authorization': `Bearer ${token}` },
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch voices: ${response.status}`)
  }
  const data = await response.json()
  // Unwrap VoiceListResponseDto envelope
  return (data.voices || []).map(mapVoiceDto)
}

// GET /v1/tts/voices/:voiceId
export async function getVoice(voiceId: string, token: string): Promise<TtsVoice> {
  const response = await fetch(`${API_BASE}/v1/tts/voices/${encodeURIComponent(voiceId)}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch voice: ${response.status}`)
  }
  const raw: TtsVoiceRaw = await response.json()
  return mapVoiceDto(raw)
}

// POST /v1/tts/:voiceId - synthesize text to speech
// Uses two-phase fetch for TTFB measurement:
// - fetch() resolves when headers arrive = TTFB
// - response.blob() resolves when full body received = totalTime
export async function synthesize(
  voiceId: string,
  request: TtsSynthesizeRequest,
  token: string
): Promise<TtsSynthesizeResponse> {
  const startTime = performance.now()

  const response = await fetch(`${API_BASE}/v1/tts/${encodeURIComponent(voiceId)}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })

  const ttfbMs = performance.now() - startTime

  if (!response.ok) {
    const status = response.status
    if (status === 401) throw new Error('Authentication required')
    if (status === 402) throw new Error('Insufficient balance. Please top up your wallet.')
    if (status === 429) throw new Error('Rate limit exceeded. Please wait and try again.')
    throw new Error(`TTS synthesis failed: ${status}`)
  }

  const audioBlob = await response.blob()
  const totalTimeMs = performance.now() - startTime

  return {
    audioBlob,
    ttfbMs: Math.round(ttfbMs),
    totalTimeMs: Math.round(totalTimeMs),
    charactersProcessed: request.text.length,
    requestId: response.headers.get('X-Request-Id') || undefined,
    audioDurationS: response.headers.get('X-Audio-Duration-S')
      ? parseFloat(response.headers.get('X-Audio-Duration-S')!)
      : undefined,
  }
}

// POST /v1/tts/voices - clone a voice (multipart form data)
// Matches AddVoiceDto: name (required), description (optional), labels (optional)
export async function cloneVoice(
  name: string,
  description: string,
  files: File[],
  token: string,
  labels?: Record<string, string>
): Promise<TtsVoice> {
  const formData = new FormData()
  formData.append('name', name)
  if (description) formData.append('description', description)
  if (labels) formData.append('labels', JSON.stringify(labels))
  files.forEach(file => formData.append('files', file))

  const response = await fetch(`${API_BASE}/v1/tts/voices`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  })

  if (!response.ok) {
    const status = response.status
    if (status === 400) throw new Error('Invalid voice data. Check file format and name.')
    if (status === 429) throw new Error('Rate limit exceeded. Please wait and try again.')
    throw new Error(`Voice cloning failed: ${status}`)
  }

  const raw: TtsVoiceRaw = await response.json()
  return mapVoiceDto(raw)
}

// DELETE /v1/tts/voices/:voiceId
export async function deleteVoice(voiceId: string, token: string): Promise<void> {
  const response = await fetch(`${API_BASE}/v1/tts/voices/${encodeURIComponent(voiceId)}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  })

  if (!response.ok) {
    if (response.status === 403) throw new Error('Cannot delete this voice - permission denied')
    throw new Error(`Failed to delete voice: ${response.status}`)
  }
}

// GET /v1/tts/health - public endpoint, no auth
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/v1/tts/health`)
    return response.ok
  } catch {
    return false
  }
}
